/**
 * Sidebar — the menu of this tool (Design's sketch "page standard, Today
 * and menu", Nino approved 12 Oct; the "One colour" look; and "the split",
 * Nino's go 27 Oct).
 *
 *   - The tool's own tile and full name on top: the Command Centre's blue
 *     "CC", the Class Creator's amber page-and-plus.
 *   - Class Creator only: one "+ New" (class, skill drill, programme).
 *   - The tool's groups (src/lib/nav.ts), then Elsewhere: the other tool
 *     (opens in a new tab, nothing travels in the link) and the business
 *     dashboard.
 *   - Counts only where someone must act (amber), from the same checks as
 *     the Today page (useWaitingCounts); Today's count is their total.
 *   - Only the page you're on is blue (a soft fill, a 3 px bar, a blue
 *     icon); every other icon is grey.
 *   - Three shapes, chosen by the Layout from the screen width: the full
 *     menu (computer), an icon rail with names on hover (iPad, 1024), and a
 *     drawer opened from the top bar's ☰ (phone), where the groups fold
 *     (Nino, 12 Oct): Today on top, the current page's group open, the
 *     others open with a tap and show their waiting count.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Plus, User as UserIcon, LogOut, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useWaitingCounts } from '@/hooks/useWaitingCounts';
import { BUSINESS_DASHBOARD_URL, NAV_GROUPS, NEW_ITEMS, isActive, navItemForPath, type CountKey, type NavItem } from '@/lib/nav';
import { TOOL, TOOL_ICON, TOOL_NAME, otherToolUrl, type Tool } from '@/lib/tool';

const OTHER: Tool = TOOL === 'class-creator' ? 'command-centre' : 'class-creator';
/** The active page's colour: blue in the Command Centre, amber in the Class Creator. */
const ACCENT = TOOL === 'class-creator'
  ? { item: 'bg-[#F5B544]/[0.12] shadow-[inset_3px_0_0_#F5B544]', icon: 'text-[#F5B544]' }
  : { item: 'bg-[#444EFF]/[0.18] shadow-[inset_3px_0_0_#444EFF]', icon: 'text-[#8b93ff]' };
/** The other tool's name in its own colour, under Elsewhere. */
const OTHER_TEXT = OTHER === 'class-creator' ? 'text-[#F5B544]' : 'text-[#8b93ff]';
const SWITCH_NOTE = `Opens the ${TOOL_NAME[OTHER]} in a new tab.`;
const SWITCH_MORE = 'The first time on this browser it asks you to sign in once; after that it opens straight away.';

export type MenuShape = 'full' | 'rail' | 'drawer';

interface SidebarProps {
  shape: MenuShape;
  /** Drawer only: close after a choice. */
  onNavigate?: () => void;
  /** Drawer only: groups fold, the current page's group open. */
  fold?: boolean;
}

export default function Sidebar({ shape, onNavigate, fold = false }: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const newRef = useRef<HTMLDivElement>(null);
  const rail = shape === 'rail';

  const waiting = useWaitingCounts();
  const currentGroup = navItemForPath(pathname)?.group ?? '';
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set([currentGroup]));
  useEffect(() => { setOpenGroups((o) => (o.has(currentGroup) ? o : new Set([...o, currentGroup]))); }, [currentGroup]);

  useEffect(() => {
    if (!newOpen) return;
    const close = (e: MouseEvent) => { if (!newRef.current?.contains(e.target as Node)) setNewOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [newOpen]);

  const countFor = (key: CountKey | undefined): number => {
    if (!key) return 0;
    const c = key === 'today' ? { state: 'ok', count: waiting.today.count } : waiting[key];
    return c.state === 'ok' ? c.count : 0;
  };

  const go = () => { setNewOpen(false); onNavigate?.(); };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderItem = (item: NavItem) => {
    const active = isActive(item, pathname);
    const n = countFor(item.count);
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={go}
        title={rail ? item.name : undefined}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center rounded-lg text-[13.5px] transition-colors',
          rail ? 'h-9 justify-center' : fold ? 'h-[38px] gap-2.5 px-2.5 text-[14.5px]' : 'h-[34px] gap-2.5 px-2.5',
          active
            ? cn(ACCENT.item, 'font-semibold text-foreground')
            : 'font-medium text-foreground/70 hover:bg-muted/50 hover:text-foreground',
        )}
      >
        <item.icon className={cn('h-[18px] w-[18px] flex-shrink-0', active ? ACCENT.icon : 'text-foreground/55')} />
        {!rail && <span className="min-w-0 flex-1 truncate">{item.name}</span>}
        {!rail && n > 0 && (
          <span className={cn('rounded-full px-[7px] text-[11px] font-bold leading-[18px]', item.count === 'live_drills' ? 'bg-[#F87171]/20 text-[#F87171]' : 'bg-amber-400/20 text-amber-500')} aria-label={item.count === 'live_drills' ? `${n} problem${n === 1 ? '' : 's'} open` : item.count === 'today' ? `${n} need someone` : `${n} waiting`}>
            {n > 99 ? '99+' : n}
          </span>
        )}
        {rail && n > 0 && <span className="sr-only">{item.count === 'live_drills' ? `${n} problem${n === 1 ? '' : 's'} open` : `${n} waiting`}</span>}
      </Link>
    );
  };

  return (
    <div className={cn('flex h-full flex-col border-r border-border bg-card', rail ? 'w-16' : 'w-[248px]')}>
      <div className={cn('flex h-[60px] flex-shrink-0 items-center gap-2.5 border-b border-border', rail ? 'justify-center' : 'px-4')}>
        <img src={TOOL_ICON[TOOL]} alt="" className="h-7 w-7 flex-shrink-0" />
        {!rail && <span className="text-base font-bold text-foreground">{TOOL_NAME[TOOL]}</span>}
      </div>

      {TOOL === 'class-creator' && <div ref={newRef} className={cn('relative', rail ? 'mx-2.5 mb-1.5 mt-3' : 'mx-3 mb-1.5 mt-3')}>
        <button
          type="button"
          onClick={() => setNewOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={newOpen}
          title={rail ? 'New' : undefined}
          className="flex h-[38px] w-full items-center justify-center gap-1.5 rounded-[10px] bg-[#F3F0EA] text-[13.5px] font-bold text-[#15151c] hover:bg-[#F3F0EA]/90"
        >
          <Plus className="h-4 w-4" />
          {!rail && 'New'}
        </button>
        {newOpen && (
          <div role="menu" className={cn('absolute z-30 mt-1 w-52 rounded-[10px] border border-border bg-popover p-1.5 shadow-xl', rail ? 'left-full top-0 ml-2 mt-0' : 'left-0 right-0 w-auto')}>
            {NEW_ITEMS.map((it) => (
              <Link key={it.href} role="menuitem" to={it.href} onClick={go} className="flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] text-foreground hover:bg-muted">
                <it.icon className="h-4 w-4 text-muted-foreground" />
                {it.name}
              </Link>
            ))}
          </div>
        )}
      </div>}

      <nav aria-label={TOOL_NAME[TOOL]} className="flex-1 overflow-y-auto overflow-x-hidden">
        {NAV_GROUPS.map((g, gi) => {
          const folds = fold && !!g.name;
          const open = !folds || openGroups.has(g.name);
          const waitingHere = g.items.reduce((sum, it) => sum + countFor(it.count), 0);
          return (
            <div key={g.name || 'top'} className={cn(rail ? 'px-2 pb-0.5 pt-1.5' : folds ? 'px-2' : 'px-2 pb-1 pt-2.5', rail && gi > 0 && 'border-t border-border')}>
              {folds ? (
                <button type="button" aria-expanded={open} onClick={() => setOpenGroups((o) => { const n = new Set(o); if (n.has(g.name)) n.delete(g.name); else n.add(g.name); return n; })}
                  className={cn('mt-1 flex h-11 w-full items-center gap-2 border-t border-border px-2.5 text-[11.5px] font-extrabold uppercase tracking-[0.14em]', open ? 'text-foreground' : 'text-foreground/55')}>
                  <span>{g.name}</span>
                  {!open && waitingHere > 0 && <span className="rounded-full bg-amber-400/20 px-[7px] text-[11px] font-bold leading-[20px] tracking-normal text-amber-500">{waitingHere}</span>}
                  <ChevronDown className={cn('ml-auto h-4 w-4 text-foreground/40 transition-transform', open && 'rotate-180')} aria-hidden />
                </button>
              ) : !rail && g.name ? (
                <div className="px-2.5 pb-1.5 pt-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-foreground/40">{g.name}</div>
              ) : null}
              {open && <div className={cn('space-y-0.5', folds && 'pb-1')}>{g.items.map((it) => renderItem(it))}</div>}
            </div>
          );
        })}
        <div className={cn(rail ? 'border-t border-border px-2 pb-2 pt-1.5' : 'px-2 pb-2 pt-2.5')}>
          {!rail && <div className="px-2.5 pb-1.5 pt-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-foreground/40">Elsewhere</div>}
          <SwitchLink rail={rail} onClick={go} />
          <a href={BUSINESS_DASHBOARD_URL} target="_blank" rel="noopener noreferrer" onClick={go} title={rail ? 'Business dashboard' : undefined}
            className={cn('flex items-center rounded-lg text-[13.5px] font-medium text-[#8b93ff] hover:bg-muted/50', rail ? 'h-9 justify-center' : 'h-[34px] gap-2.5 px-2.5')}>
            <ExternalLink className="h-[18px] w-[18px] flex-shrink-0" />
            {!rail && <><span className="min-w-0 flex-1 truncate">Business dashboard</span><span aria-hidden className="text-foreground/40">↗</span></>}
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </div>
      </nav>

      <div className="relative flex-shrink-0 border-t border-border">
        <button
          type="button"
          onClick={() => setUserMenuOpen((o) => !o)}
          aria-label="Account menu"
          className={cn('flex h-14 w-full items-center gap-2.5 text-[13px] text-foreground hover:bg-muted/40', rail ? 'justify-center' : 'px-3.5')}
        >
          <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-foreground">
            <UserIcon className="h-4 w-4 text-background" />
          </span>
          {!rail && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{user?.email ?? 'Signed in'}</span>
              <ChevronUp className={cn('h-4 w-4 text-muted-foreground transition-transform', userMenuOpen ? '' : 'rotate-180')} />
            </>
          )}
        </button>
        {userMenuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
            <div className={cn('absolute bottom-full z-20 mb-1 rounded-md border border-border bg-card shadow-lg', rail ? 'left-full ml-1 w-48' : 'left-2 right-2')}>
              <div className="truncate px-3 py-2 text-xs text-muted-foreground">{user?.email ?? 'Signed in'}</div>
              <div className="border-t border-border" />
              <button type="button" onClick={handleLogout} className="flex w-full items-center px-3 py-2 text-sm text-foreground hover:bg-muted">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The other tool, under Elsewhere (Design's sketch "the split"): its own tile, its name and ↗; it
 * opens in a new tab. On hover, a note says the first visit asks for a sign-in once. Nothing
 * travels in the link. Every signed-in staff member has both tools today, so it always shows.
 */
function SwitchLink({ rail, onClick }: { rail: boolean; onClick: () => void }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const show = () => { const r = ref.current?.getBoundingClientRect(); if (r) setAt({ left: r.right + 10, top: r.top }); };
  const hide = () => setAt(null);
  return (
    <>
      <a ref={ref} href={otherToolUrl(OTHER)} target="_blank" rel="noopener noreferrer" onClick={onClick}
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
        aria-describedby="switch-note"
        className={cn('flex items-center rounded-lg text-[13.5px] font-medium hover:bg-muted/50', OTHER_TEXT, rail ? 'h-9 justify-center' : 'h-[34px] gap-2.5 px-2.5')}>
        <img src={TOOL_ICON[OTHER]} alt="" className="h-[18px] w-[18px] flex-shrink-0" />
        {!rail && <><span className="min-w-0 flex-1 truncate">{TOOL_NAME[OTHER]}</span><span aria-hidden className="text-foreground/40">↗</span></>}
        {rail && <span className="sr-only">{TOOL_NAME[OTHER]}</span>}
      </a>
      <span id="switch-note" className="sr-only">{SWITCH_NOTE} {SWITCH_MORE}</span>
      {at && createPortal(
        <div role="tooltip" aria-hidden className="pointer-events-none fixed z-50 w-[300px] rounded-[10px] border border-border bg-popover px-3.5 py-2.5 text-[12.5px] leading-[1.45] text-foreground/70 shadow-xl" style={at}>
          <b className="font-semibold text-foreground">{SWITCH_NOTE}</b> {SWITCH_MORE}
        </div>,
        document.body,
      )}
    </>
  );
}
